import { getOpenAIClient, isOpenAIConfigured } from './openaiClient';

/**
 * Generates a personalized career development plan using OpenAI GPT.
 * @param {Object} assessmentData - User's assessment data including occupation, seniority, and responses.
 * @returns {Promise<Object>} Generated career development plan.
 */
export async function generateCareerPlan(assessmentData) {
  try {
    // Check if OpenAI is configured
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();
    const { occupation, seniority, industry, location = {}, responses = {}, userId } = assessmentData;
    
    // Create comprehensive prompt based on assessment data
    const systemPrompt = `You are an expert career development advisor specializing in creating personalized career advancement plans. Generate a comprehensive, actionable career development plan based on the user's assessment data.

Focus on practical, measurable goals and provide specific, implementable strategies. The plan should be professional, motivating, and tailored to the user's current level and career goals.

Return a structured JSON response with the exact format specified.`;

    // Build location context for the prompt
    let locationContext = '';
    if (location?.currentZipCode) {
      locationContext += `- Current Location: ZIP code ${location.currentZipCode}\n`;
    }
    if (location?.locationPreference && location?.locationPreference !== 'current_location') {
      locationContext += `- Location Preference: ${location.locationPreference}\n`;
      if (location?.targetLocation) {
        locationContext += `- Target Location: ${location.targetLocation}\n`;
      }
    } else {
      locationContext += `- Location Preference: Stay in current location\n`;
    }

    const userPrompt = `Create a personalized career development plan for:
- Occupation: ${occupation}
- Industry: ${industry || 'Technology'}
- Current Level: ${seniority}
${locationContext}- Career Goals: ${responses?.careerGoals || 'Professional advancement and skill development'}
- Current Challenges: ${responses?.challenges || 'Skill gaps and career progression'}
- Preferred Learning Style: ${responses?.learningStyle || 'Mixed approach'}
- Time Commitment: ${responses?.timeCommitment || '3-5 hours per week'}

Please create a comprehensive plan with realistic timelines and actionable steps. Include location-specific recommendations such as:
- Local networking opportunities and professional events
- Regional salary insights and market conditions
- Local companies and organizations relevant to the role
- Remote work opportunities if applicable
- Cost of living considerations for career advancement goals`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini', // Use reliable model that supports structured outputs
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 4000,
      temperature: 0.7,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'career_development_plan',
          schema: {
            type: 'object',
            properties: {
              occupation: { type: 'string' },
              seniority: { type: 'string' },
              estimatedWeeks: { type: 'number' },
              estimatedHoursPerWeek: { type: 'string' },
              totalExercises: { type: 'number' },
              sections: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string', enum: ['summary', 'skills', 'mindset', 'networking', 'weekly_plan', 'kpis'] },
                    content: { type: 'string' },
                    items: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          title: { type: 'string' },
                          description: { type: 'string' },
                          timeframe: { type: 'string' },
                          priority: { type: 'string', enum: ['High', 'Medium', 'Low'] }
                        },
                        required: ['title', 'description', 'timeframe', 'priority']
                      }
                    },
                    metrics: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          name: { type: 'string' },
                          description: { type: 'string' },
                          target: { type: 'string' }
                        },
                        required: ['name', 'description', 'target']
                      }
                    }
                  },
                  required: ['type', 'content']
                }
              }
            },
            required: ['occupation', 'seniority', 'estimatedWeeks', 'estimatedHoursPerWeek', 'totalExercises', 'sections'],
            additionalProperties: false,
          },
        },
      },
    });

    let planData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Enrich plan with metadata
    return {
      id: `plan_${Date.now()}`,
      userId: userId,
      createdAt: new Date(),
      lastUpdated: new Date(),
      isSaved: false,
      ...planData
    };
  } catch (error) {
    console.error('Error generating career plan:', error);
    
    // Provide more specific error messages
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else if (error?.status === 429) {
      throw new Error('OpenAI API quota exceeded. Please check your usage limits.');
    } else {
      throw new Error('Failed to generate career plan. Please try again later.');
    }
  }
}

/**
 * Regenerates a career development plan with new insights and improved error handling.
 * @param {Object} existingPlan - The current plan data.
 * @param {Object} userFeedback - Optional user feedback for improvements.
 * @returns {Promise<Object>} Updated career development plan.
 */
export async function regenerateCareerPlan(existingPlan, userFeedback = {}) {
  try {
    // Check if OpenAI is configured
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    // Validate existing plan data
    if (!existingPlan) {
      throw new Error('No existing plan provided for regeneration.');
    }

    const openai = getOpenAIClient();

    // Enhanced system prompt for better regeneration
    const systemPrompt = `You are an expert career development advisor specializing in regenerating and improving career plans. The user wants to regenerate their existing career plan with improvements based on their feedback and current progress.

Key responsibilities:
1. Analyze the existing plan structure and content
2. Incorporate user feedback and focus areas
3. Maintain successful strategies while improving weak areas
4. Ensure the regenerated plan is more targeted and actionable
5. Consider current market trends and industry best practices

Return a structured JSON response with the exact format specified, ensuring all sections are comprehensive and actionable.`;

    // Enhanced user prompt with more context
    const userPrompt = `Please regenerate and significantly improve this career development plan based on the following information:

EXISTING PLAN DETAILS:
- Occupation: ${existingPlan?.occupation || 'Not specified'}
- Current Level: ${existingPlan?.seniority || 'Not specified'}
- Current Timeline: ${existingPlan?.estimatedWeeks || 'Not specified'} weeks
- Weekly Commitment: ${existingPlan?.estimatedHoursPerWeek || 'Not specified'} hours
- Current Sections: ${existingPlan?.sections?.map(s => s?.type)?.join(', ') || 'None'}

USER FEEDBACK & REQUIREMENTS:
- Comments: ${userFeedback?.comments || 'User requested general improvements and optimization'}
- Focus Areas: ${userFeedback?.focusAreas?.join(', ') || 'Overall plan enhancement, current industry trends, practical implementation'}
- Specific Concerns: ${userFeedback?.concerns || 'None specified'}

REGENERATION GOALS:
1. Make the plan more specific and actionable
2. Include current industry trends and best practices
3. Improve time allocation and milestone tracking
4. Add more practical exercises and real-world applications
5. Enhance skill development pathways

Please create a significantly improved version that addresses all feedback and provides a more comprehensive, modern approach to career development.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 4000,
      temperature: 0.8, // Slightly higher temperature for more creative improvements
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'career_development_plan',
          schema: {
            type: 'object',
            properties: {
              occupation: { type: 'string' },
              seniority: { type: 'string' },
              estimatedWeeks: { type: 'number' },
              estimatedHoursPerWeek: { type: 'string' },
              totalExercises: { type: 'number' },
              sections: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string', enum: ['summary', 'skills', 'mindset', 'networking', 'weekly_plan', 'kpis'] },
                    content: { type: 'string' },
                    items: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          title: { type: 'string' },
                          description: { type: 'string' },
                          timeframe: { type: 'string' },
                          priority: { type: 'string', enum: ['High', 'Medium', 'Low'] }
                        },
                        required: ['title', 'description', 'timeframe', 'priority']
                      }
                    },
                    metrics: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          name: { type: 'string' },
                          description: { type: 'string' },
                          target: { type: 'string' }
                        },
                        required: ['name', 'description', 'target']
                      }
                    }
                  },
                  required: ['type', 'content']
                }
              }
            },
            required: ['occupation', 'seniority', 'estimatedWeeks', 'estimatedHoursPerWeek', 'totalExercises', 'sections'],
            additionalProperties: false,
          },
        },
      },
    });

    if (!response?.choices?.[0]?.message?.content) {
      throw new Error('Invalid response from OpenAI API - no content received.');
    }

    let planData;
    try {
      planData = JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', parseError);
      throw new Error('Failed to parse AI response. The generated plan may be malformed.');
    }

    // Validate that essential fields are present
    if (!planData?.sections || !Array.isArray(planData?.sections) || planData?.sections?.length === 0) {
      throw new Error('Generated plan is missing essential sections. Please try again.');
    }
    
    // Preserve original metadata but update content and timestamp
    const regeneratedPlan = {
      ...existingPlan,
      ...planData,
      lastUpdated: new Date(),
      isSaved: false, // Mark as unsaved since it's been modified
      regenerationCount: (existingPlan?.regenerationCount || 0) + 1,
      feedback: userFeedback // Store the feedback used for regeneration
    };

    console.log('Plan regenerated successfully with OpenAI');
    return regeneratedPlan;
    
  } catch (error) {
    console.error('Error regenerating career plan:', error);
    
    // Provide more specific error messages based on error type
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else if (error?.status === 429) {
      throw new Error('OpenAI API quota exceeded. Please check your usage limits or try again later.');
    } else if (error?.status === 503 || error?.status === 502) {
      throw new Error('OpenAI API is temporarily unavailable. Please try again in a few minutes.');
    } else if (error?.message?.includes('parse')) {
      throw error;
    } else if (error?.message?.includes('missing essential sections')) {
      throw error;
    } else {
      throw new Error('Failed to regenerate career plan. Please try again later.');
    }
  }
}

/**
 * Analyzes user progress and provides personalized recommendations.
 * @param {Object} planData - Current career plan.
 * @param {Object} progressData - User's progress data.
 * @returns {Promise<Object>} Personalized recommendations and insights.
 */
export async function getPersonalizedRecommendations(planData, progressData = {}) {
  try {
    // Check if OpenAI is configured
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();

    const systemPrompt = `You are an expert career coach analyzing a user's progress on their career development plan. Provide personalized, actionable recommendations based on their current progress and plan.

Focus on specific next steps, areas of improvement, and motivational insights. Be encouraging but realistic.`;

    const userPrompt = `Analyze this career development progress and provide recommendations:

Career Plan: ${planData?.occupation} advancing to ${planData?.seniority}+ level
Current Progress:
- Completed Exercises: ${progressData?.completedExercises || 0} of ${planData?.totalExercises}
- Weeks Active: ${progressData?.weeksActive || 0} of ${planData?.estimatedWeeks}
- Current Focus Areas: ${progressData?.currentFocusAreas?.join(', ') || 'Getting started'}
- Recent Achievements: ${progressData?.achievements?.join(', ') || 'Beginning journey'}

Please provide specific recommendations for the next 2 weeks.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 2000,
      temperature: 0.7,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'personalized_recommendations',
          schema: {
            type: 'object',
            properties: {
              overallProgress: { type: 'string' },
              nextSteps: {
                type: 'array',
                items: { type: 'string' }
              },
              focusAreas: {
                type: 'array',
                items: { type: 'string' }
              },
              motivationalMessage: { type: 'string' },
              adjustments: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    area: { type: 'string' },
                    suggestion: { type: 'string' }
                  },
                  required: ['area', 'suggestion']
                }
              }
            },
            required: ['overallProgress', 'nextSteps', 'focusAreas', 'motivationalMessage'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error getting personalized recommendations:', error);
    
    // Provide more specific error messages
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else {
      throw new Error('Failed to get personalized recommendations. Please try again later.');
    }
  }
}

/**
 * Validates if OpenAI API is properly configured and accessible.
 * @returns {Promise<boolean>} True if API is accessible, false otherwise.
 */
export async function validateOpenAIConnection() {
  try {
    // Check if client is configured
    if (!isOpenAIConfigured()) {
      console.warn('OpenAI API key not found or not configured');
      return false;
    }

    const openai = getOpenAIClient();
    
    // Make a minimal API call to test connection
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo', // Use cheapest model for validation
      messages: [
        { role: 'user', content: 'Hello' }
      ],
      max_tokens: 5,
    });

    return response?.choices?.[0]?.message?.content ? true : false;
  } catch (error) {
    console.error('OpenAI API validation failed:', error);
    
    // Log specific error details for debugging
    if (error?.status === 401) {
      console.error('Authentication failed - Invalid API key');
    } else if (error?.status === 429) {
      console.error('Rate limit exceeded');
    } else if (error?.status === 403) {
      console.error('Access forbidden - Check API permissions');
    }
    
    return false;
  }
}